/**
 * Utility functions for contexts
 * */
import { createContext, useContext } from 'react';

/**
 * ------------------------------------
 *  Create a safe context. Use this instead of `createContext` from `react` wherever possible
 *
 *  Creating contexts:
 * ```typescriptreact
 * const [FloatingDockContextProvider, useFloatingDockContext] =
 *   createSafeContext<{ mouseX: number }>(
 *     'useFloatingDockContext must be used within a FloatingDockProvider'
 *   );
 * ```
 *
 * using context:
 * ```typescriptreact
 * const { mouseX } = useFloatingDockContext();
 * ```
 * */
export function createSafeContext<ContextValue>(errorMessage: string) {
  const Context = createContext<ContextValue | null>(null);

  const useSafeContext = () => {
    const ctx = useContext(Context);

    if (ctx === null) {
      throw new Error(errorMessage);
    }

    return ctx;
  };

  const Provider = ({
    children,
    value,
  }: {
    value: ContextValue;
    children: React.ReactNode;
  }) => <Context.Provider value={value}>{children}</Context.Provider>;

  return [Provider, useSafeContext] as const;
}
